'use client';

import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { Modals } from '@/helpers/modals';
import { useGetCommonStore, useModalsStore } from '@/store';
import { type BonusShopTab, getSections } from '../../helpers';
import { ScBonusShopGuestCta } from '../../styled';
import type { BonusShopProduct } from '../../types';
import { BonusShopSlider } from '../BonusShopSlider';

interface BonusShopSectionsProps {
  products: BonusShopProduct[];
  onShowAll: (tab: BonusShopTab) => void;
}

const GUEST_CTA_AFTER_SECTION = 1;

export const BonusShopSections = ({
  products,
  onShowAll,
}: BonusShopSectionsProps) => {
  const { t } = useTranslation('bonusShop');
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const openModal = useModalsStore.use.open();

  const sections = useMemo(
    () =>
      getSections(t)
        .map((section) => ({
          ...section,
          items: products.filter(({ type }) => type === section.type),
        }))
        .filter(({ items }) => items.length),
    [products, t]
  );

  const guestCtaIndex = Math.min(GUEST_CTA_AFTER_SECTION, sections.length - 1);

  return (
    <div className="sections">
      {sections.map(({ tab, type, title, items }, index) => (
        <Fragment key={type}>
          <BonusShopSlider
            onShowAll={() => onShowAll(tab)}
            products={items}
            title={title}
          />
          {!isAuthenticated && index === guestCtaIndex && (
            <ScBonusShopGuestCta>
              <Button onClick={() => openModal(Modals.Registration)} primary>
                {t('signUpToExchange')}
              </Button>
            </ScBonusShopGuestCta>
          )}
        </Fragment>
      ))}
    </div>
  );
};
